import React from 'react';
import moment from 'moment';
import { FormGroup, Input, Alert } from 'reactstrap';
import Page from '../../containers/page';
import RouteComponent from '../../helpers/route';
import { ApiLogsOverviewTab, ApiLogsRoutesTab, ApiLogsDatabaseTab, ApiLogsCallersTab } from '../../components/apiLogsAnalytics';
import ApiLogsList from '../../components/apiLogsList';
import ApiLogsLive from '../../components/apiLogsLive';
import ApiLogsReplicasTab from '../../components/apiLogsReplicas';
import {
    OVERVIEW_PERIODS, MAX_OVERVIEW_DAYS, overviewRange, fetchApiLogsOverview, isIntervalAllowed,
} from '../../helpers/apiLogs';

/**
* "API analytics (all requests)": every request on the backend, from the
* `logs` collection. Overview / Routes / Database / Who calls us all render one
* `/data/api-logs/overview` response (hourly/daily summaries, up to a year),
* fetched once per period and shared. Logs reads raw logs, at most 24 h.
* Tab and period live in the URL.
*/

const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'routes', label: 'Routes' },
    { key: 'database', label: 'Database' },
    { key: 'replicas', label: 'Replicas' },
    { key: 'callers', label: 'Who calls us' },
    { key: 'logs', label: 'Logs' },
];

const styles = {
    filterBar: {
        display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 14,
        backgroundColor: '#FFFFFF', borderRadius: 6, padding: '16px 18px',
        boxShadow: '0 10px 20px rgba(0,0,0,0.06)', marginBottom: 8,
    },
    filterGroup: { marginBottom: 0, minWidth: 140 },
    filterLabel: { fontSize: 11, fontWeight: 600, color: '#818FA5', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 },
    coverage: { fontSize: 12, color: '#818FA5', marginBottom: 14, minHeight: 18 },
    emptyState: { textAlign: 'center', padding: '40px 20px', color: '#818FA5', fontSize: 14 },
};

class Screen extends RouteComponent {
    constructor(props) {
        super(props);
        this.state = {
            ...this.state,
            overview: null,
            overviewKey: null,
            overviewLoading: false,
            overviewError: null,
            // the period of the data on screen: when only the chart interval changes,
            // that data stays up while the new interval loads
            overviewRangeKey: null,
            logsFilters: null,
            logsFilterToken: 0,
            // a route opened from the Database tab: Routes searches for it and opens its panel
            routesFocus: null,
        };
        this._rangeCache = null;
    }

    componentDidMount() {
        if (super.componentDidMount) super.componentDidMount();
        this._loadOverview();
    }

    componentDidUpdate(prevProps, prevState) {
        if (super.componentDidUpdate) super.componentDidUpdate(prevProps, prevState);
        this._loadOverview();
    }

    _period = () => {
        let params = this._getParams();
        return { preset: params.preset || '30d', customFrom: params.customFrom || '', customTo: params.customTo || '' };
    }

    _tab = () => {
        let params = this._getParams();
        return TABS.find((t) => t.key === params.tab) ? params.tab : 'overview';
    }

    // relative to "today", so cache per period - a new range object every render would refetch forever
    _range = () => {
        let period = this._period();
        let key = JSON.stringify(period);
        if (!this._rangeCache || this._rangeCache.key !== key) {
            this._rangeCache = { key, value: overviewRange(period) };
        }
        return this._rangeCache.value;
    }

    _loadOverview = () => {
        if (this._tab() === 'logs') return;

        let range = this._range();
        if (!range || range.days > MAX_OVERVIEW_DAYS || range.days < 1) return;

        let interval = this._interval();
        let rangeKey = `${range.from}|${range.to}`;
        let key = `${rangeKey}|${interval}`;
        if (key === this.state.overviewKey) return;

        this.setState({ overviewKey: key, overviewLoading: true, overviewError: null });
        fetchApiLogsOverview({ from: range.from, to: range.to, interval: interval || undefined }).then(({ data, error }) => {
            // a newer period / interval was picked while this one was loading
            if (this.state.overviewKey !== key) return;
            if (error && this.state.overviewRangeKey === rangeKey) {
                // e.g. a 400 for an interval: keep the data that is already up
                this.setState({ overviewError: error, overviewLoading: false });
                return;
            }
            this.setState({ overview: data, overviewError: error, overviewLoading: false, overviewRangeKey: error ? null : rangeKey });
        });
    }

    // the chart interval from the URL, or '' (backend default) when the period is too long for it
    _interval = () => {
        let params = this._getParams();
        let range = this._range();
        return params.interval && range && isIntervalAllowed(params.interval, range.days) ? params.interval : '';
    }

    _openLogsAround = (at) => {
        let t = moment(at);
        this._openLogs({
            preset: 'custom',
            customFrom: moment(t).subtract(5, 'minutes').format('YYYY-MM-DDTHH:mm'),
            customTo: moment(t).add(5, 'minutes').format('YYYY-MM-DDTHH:mm'),
        });
    }

    _setParams = (patch) => {
        this.updateMultipleParams(Object.keys(patch).map((name) => ({ name, value: patch[name] })), false);
    }

    _openLogs = (filters) => {
        this.setState((prev) => ({ logsFilters: { preset: '24h', ...filters }, logsFilterToken: prev.logsFilterToken + 1 }));
        this._setParams({ tab: 'logs' });
    }

    // logs are now written with their route template, so filter by it exactly
    _onOpenRoute = (row) => {
        this._openLogs({ method: row.method || '', route: row.route, sortField: 'executionTime', sortType: -1 });
    }

    _focusRoute = (row) => {
        this.setState({ routesFocus: { method: row.method, route: row.route, token: Date.now() } });
        this._setParams({ tab: 'routes' });
    }

    _renderPeriodPicker = () => {
        let lang = this.props.lang;
        let period = this._period();

        return (
            <div style={styles.filterBar}>
                <FormGroup style={styles.filterGroup}>
                    <div style={styles.filterLabel}>{'Period'.translate(lang)}</div>
                    <Input type="select" style={{ height: 42 }} value={period.preset} onChange={(e) => this._setParams({ preset: e.target.value })}>
                        {OVERVIEW_PERIODS.map((p) => <option key={p.value} value={p.value}>{p.label.translate(lang)}</option>)}
                    </Input>
                </FormGroup>
                {period.preset === 'custom' ? (
                    <>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'From'.translate(lang)}</div>
                            <Input type="date" style={{ height: 42 }} value={period.customFrom} onChange={(e) => this._setParams({ customFrom: e.target.value })} />
                        </FormGroup>
                        <FormGroup style={styles.filterGroup}>
                            <div style={styles.filterLabel}>{'To'.translate(lang)}</div>
                            <Input type="date" style={{ height: 42 }} value={period.customTo} onChange={(e) => this._setParams({ customTo: e.target.value })} />
                        </FormGroup>
                    </>
                ) : null}
            </div>
        );
    }

    _renderCoverage = () => {
        let lang = this.props.lang;
        let data = this.state.overview;
        let range = this._range();
        let coverage = data && data.coverage;

        if (!coverage) return <div style={styles.coverage} />;

        let before = range && coverage.dataSince && range.from < coverage.dataSince;

        return (
            <div style={styles.coverage}>
                {coverage.updatedAt ? `${'Data updated'.translate(lang)}: ${moment(coverage.updatedAt).format('YYYY-MM-DD HH:mm')}` : null}
                {coverage.updatedAt ? ` · ${'the last ~10 minutes are not in the analytics yet, the Logs tab is always live.'.translate(lang)}` : null}
                {before ? (
                    <div style={{ color: '#a86f00', fontWeight: 600 }}>
                        {`▲ ${'Data exists from'.translate(lang)} ${coverage.dataSince} - ${'earlier days of this period are not shown.'.translate(lang)}`}
                    </div>
                ) : null}
            </div>
        );
    }

    render() {
        let lang = this.props.lang;
        let tab = this._tab();
        let period = this._period();
        let range = this._range();
        let { overview, overviewLoading, overviewError } = this.state;
        let rangeIncomplete = period.preset === 'custom' && !range;
        let rangeInvalid = !!range && (range.days > MAX_OVERVIEW_DAYS || range.days < 1);
        let showOverview = tab !== 'logs' && range && !rangeInvalid;
        // only the chart interval changed: keep the current data up while it loads
        let softLoading = overviewLoading && !!overview && range && this.state.overviewRangeKey === `${range.from}|${range.to}`;
        let avgPerSecond = overview && overview.totals && overview.totals.rates ? overview.totals.rates.avgPerSecond : null;

        return (
            <div>
                <div className="section-title title-options">
                    <h1>{'API Analytics (all requests)'.translate(lang)}</h1>
                </div>

                <ApiLogsLive avgPerSecond={avgPerSecond} lang={lang} />

                {tab !== 'logs' ? this._renderPeriodPicker() : null}
                {tab !== 'logs' ? this._renderCoverage() : null}

                <div className="table-tabs">
                    {TABS.map((t) => (
                        <button key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => { this.setState({ routesFocus: null }); this._setParams({ tab: t.key }); }}>
                            {t.label.translate(lang)}
                        </button>
                    ))}
                </div>

                <div style={{ marginTop: 14 }}>
                    {tab !== 'logs' && rangeIncomplete ? <Alert color="info">{'Pick both a from and a to date for a custom period.'.translate(lang)}</Alert> : null}
                    {tab !== 'logs' && rangeInvalid ? (
                        <Alert color="warning">{`${'The period must be between 1 and'.translate(lang)} ${MAX_OVERVIEW_DAYS} ${'days'.translate(lang)}.`}</Alert>
                    ) : null}
                    {showOverview && overviewError ? <Alert color="danger">{overviewError}</Alert> : null}
                    {showOverview && overviewLoading && !softLoading ? <div style={styles.emptyState}>{'Loading...'.translate(lang)}</div> : null}

                    {showOverview && (!overviewLoading || softLoading) && overview ? (
                        <>
                            {tab === 'overview' ? (
                                <ApiLogsOverviewTab
                                    data={overview}
                                    rangeDays={range.days}
                                    onChartInterval={(interval) => this._setParams({ interval })}
                                    chartLoading={softLoading}
                                    onOpenLogsAround={this._openLogsAround}
                                    lang={lang}
                                />
                            ) : null}
                            {tab === 'routes' ? <ApiLogsRoutesTab data={overview} onOpenRoute={this._onOpenRoute} focus={this.state.routesFocus} lang={lang} /> : null}
                            {tab === 'replicas' ? <ApiLogsReplicasTab data={overview} onOpenReplica={(replica) => this._openLogs({ replica: String(replica) })} lang={lang} /> : null}
                            {tab === 'database' ? <ApiLogsDatabaseTab data={overview} onOpenRoute={this._focusRoute} lang={lang} /> : null}
                            {tab === 'callers' ? <ApiLogsCallersTab data={overview} onOpenLogs={this._openLogs} lang={lang} /> : null}
                        </>
                    ) : null}

                    {tab === 'logs' ? (
                        <ApiLogsList
                            lang={lang}
                            initialFilters={this.state.logsFilters}
                            resetToken={this.state.logsFilterToken}
                            replicaSlots={overview && overview.byReplica ? overview.byReplica.map((r) => r.replica).filter((r) => r !== null && r !== undefined) : []}
                        />
                    ) : null}
                </div>
            </div>
        );
    }
}

export default Page(Screen);
